import React, { useMemo, useCallback } from 'react';

import { useGetEventsInfinite } from '@vlabs/api/artemy/client';
import type { EventRecord } from '@vlabs/api/artemy/model';
import { useIntersectionObserver } from '@vlabs/shared/hooks';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { useTranslation } from 'react-i18next';

import { EVENT_TARGET } from '@vlabs/monokit/entities/events/api';

import { EventHistoryCardPoint3 } from './EventHistoryCard.point3';
import st from './EventTopMatchHistory.module.sass';

const PAGE_SIZE = 10;

interface EventTopMatchHistoryPoint3Props {
  event?: EventRecord;
  onEventSelect: (event: EventRecord) => void;
}

export function EventTopMatchHistoryPoint3({
  event = undefined,
  onEventSelect,
}: EventTopMatchHistoryPoint3Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const topMatch = event?.top_match;
  const topMatchId = useMemo(() => {
    if (!topMatch) return undefined;
    const id = 'face_id' in topMatch ? topMatch.face_id : topMatch.event_id;
    return id ?? undefined;
  }, [topMatch]);

  const getEvents = useGetEventsInfinite(
    {
      top_similar_object_ids: topMatchId ? [topMatchId] : undefined,
      page_size: PAGE_SIZE,
      page: 1,
      target: EVENT_TARGET,
    },
    {
      query: {
        enabled: !!topMatchId,
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
          const eventsCount = lastPage.events?.length ?? 0;

          return eventsCount < PAGE_SIZE
            ? undefined
            : allPages.length + 1;
        },
      },
    },
  );

  const eventHistory = useMemo(
    () => getEvents.data?.pages.flatMap((page) => page.events ?? []) ?? [],
    [getEvents.data],
  );

  const handleScroll = useCallback(() => {
    if (getEvents.hasNextPage && !getEvents.isFetchingNextPage) {
      getEvents.fetchNextPage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEvents.fetchNextPage, getEvents.hasNextPage, getEvents.isFetchingNextPage]);

  const ref = useIntersectionObserver(handleScroll);

  if (getEvents.isLoading) {
    return (
      <div className={st.EmptyHistoryPanel}>
        <Loader />
      </div>
    );
  }

  if (eventHistory.length === 0 || !topMatchId) {
    return (
      <div className={st.EmptyHistoryPanel}>
        {t('lunapoint:панель оператора.event history.no event history')}
      </div>
    );
  }

  return (
    <div className={st.Root}>
      {eventHistory.map((historyEvent) => (
        <EventHistoryCardPoint3
          event={historyEvent}
          isActive={historyEvent.event_id === event?.event_id}
          key={historyEvent.event_id}
          onClick={onEventSelect}
        />
      ))}

      {!!getEvents.hasNextPage && (
        <div
          className={st.Loader}
          ref={ref}
        >
          {!!getEvents.isFetchingNextPage && <Loader />}
        </div>
      )}
    </div>
  );
}
